import { Box, Flex, LegacyText, themedColor } from '@lemonade-hq/cdk';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { ThemeColorName } from '@lemonade-hq/nuis-atomic';
import { VERTICAL_MEDIA_QUERIES } from './verticalMediaQueries';
import { Play, Pause, Mute, Unmute, CaptionsOff, CaptionsOn } from './Icons';

const ReviewerName = styled(LegacyText)`
  margin-bottom: 0;

  font-size: 1.8rem;
  margin-left: 12px;

  ${VERTICAL_MEDIA_QUERIES.med`
        font-size: 1.6rem;
        margin-left: 8px;
    `}
`;

const PlayerButton = styled(Box).attrs({ role: 'button', tabIndex: 0 })<{ hoverRatio?: number }>`
  width: 24px;
  height: 24px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    color: ${themedColor('primary')};
    scale: ${({ hoverRatio }: { hoverRatio?: number }) => hoverRatio ?? 1.1};
  }

  &:last-of-type {
    margin-left: 12px;

    ${VERTICAL_MEDIA_QUERIES.med`
            margin-left: 8px;
        `}
  }
`;

const Wrapper = styled.div<{ textColor: ThemeColorName }>`
  color: ${({ textColor }) => themedColor(textColor)};
  position: absolute;
  bottom: 50px;
  left: 0;
  width: 100%;
  padding-inline: 4rem;
  gap: 8px;

  display: flex;
  flex-direction: column;

  ${VERTICAL_MEDIA_QUERIES.med`
        bottom: 36px;
        padding-inline: 2.6rem;
    `}
`;

const Progress = styled.div`
  height: 4px;
  width: 100%;
  background-color: ${themedColor('light')};
  border-radius: 100px;
  position: relative;
  transition: height 0.1s ease-in-out;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background-color: ${themedColor('primary')};
    border-radius: 100px;
    width: 100%;
    max-width: var(--progress-width, 0%);
    transition: max-width 0.25s linear;
  }
`;

const Seek = styled.div`
  position: absolute;
  scale: 0;
  transition: scale 0.1s ease-in-out;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 10px;
  aspect-ratio: 1/1;
  border-radius: 50%;
  background-color: ${themedColor('primary')};
`;

const ProgressWrapper = styled.div`
  position: relative;
  height: 12px;
  display: flex;
  align-items: center;

  &:hover {
    ${Progress} {
      height: 8px;
    }

    ${Seek} {
      scale: 1;
    }
  }
`;

interface ControlsProps {
  progress: number;
  seekProgress: (progress: number) => void;
  togglePlay: () => void;
  isPaused: boolean;
  isMuted?: boolean;
  setIsMuted?: (isMuted: boolean) => void;
  captionsOn?: boolean;
  setCaptionsOn?: (isMuted: boolean) => void;
  reviewerName?: string;
  source?: string;
}

export const Controls: React.FC<ControlsProps> = ({
  progress,
  togglePlay,
  isPaused,
  isMuted,
  setIsMuted,
  captionsOn,
  setCaptionsOn,
  reviewerName,
  source,
  seekProgress,
}) => {
  const progressRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const seekPositionCalculated = useMemo(() => {
    if (progressRef.current) {
      return progressRef.current.getBoundingClientRect().width * (progress / 100);
    }
    return 0;
  }, [progress]);

  const calculateProgress = useCallback((clientX: number): number => {
    if (!progressRef.current) return 0;

    const rect = progressRef.current.getBoundingClientRect();
    const clampedPosition = Math.max(0, Math.min(clientX - rect.left, rect.width));
    return clampedPosition / rect.width;
  }, []);

  const startDragging = useCallback(
    (clientX: number) => {
      setIsDragging(true);
      const newProgress = calculateProgress(clientX);
      seekProgress(newProgress);
    },
    [calculateProgress, seekProgress],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      startDragging(e.clientX);
    },
    [startDragging],
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      startDragging(e.touches[0].clientX);
    },
    [startDragging],
  );

  const handleMove = useCallback(
    (clientX: number) => {
      if (!isDragging || !progressRef.current) return;

      const newProgress = calculateProgress(clientX);
      seekProgress(newProgress);
    },
    [isDragging, calculateProgress, seekProgress],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      handleMove(e.clientX);
    },
    [handleMove],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      handleMove(e.touches[0].clientX);
    },
    [handleMove],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  return (
    <Wrapper textColor={setIsMuted != null ? 'white' : 'boulder'}>
      <Flex alignItems="center">
        <PlayerButton onClick={togglePlay} aria-label={isPaused ? 'Play' : 'Pause'}>
          {isPaused ? <Play /> : <Pause />}
        </PlayerButton>
        <ReviewerName type="main" lineHeight={[1.4]} fontWeight={600}>
          {reviewerName}
          {source != null && (
            <>
              ,&nbsp;
              {source}
            </>
          )}
        </ReviewerName>

        <PlayerButton
          onClick={() => (setCaptionsOn != null ? setCaptionsOn(!captionsOn) : null)}
          aria-label={captionsOn ? 'Hide captions' : 'Show captions'}
          ml="auto"
          hoverRatio={1.05}
          style={{
            opacity: setCaptionsOn != null ? 1 : 0,
            pointerEvents: setCaptionsOn != null ? 'auto' : 'none',
          }}
        >
          {captionsOn ? <CaptionsOn /> : <CaptionsOff />}
        </PlayerButton>

        {setIsMuted != null && (
          <PlayerButton onClick={() => setIsMuted(!isMuted)} aria-label={isMuted ? 'Mute' : 'Unmute'}>
            {isMuted ? <Mute /> : <Unmute />}
          </PlayerButton>
        )}
      </Flex>
      <ProgressWrapper
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onMouseMove={handleMouseMove}
        onTouchMove={handleTouchMove}
        onMouseUp={handleMouseUp}
        onTouchEnd={handleMouseUp}
        ref={progressRef}
      >
        <Progress style={{ '--progress-width': `${progress}%` } as React.CSSProperties} />
        <Seek style={{ left: seekPositionCalculated }} />
      </ProgressWrapper>
    </Wrapper>
  );
};
